import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Loader2, Plus, Percent } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const EMPTY = { name: "", promotion_type: "", promotional_price: "", promotion_start_date: "", promotion_end_date: "" };

export default function PromotionsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [zones, setZones] = useState([]);
  const [zoneFilter, setZoneFilter] = useState({ state: ALL, status: "AVAILABLE", classification: ALL });
  const [filterMeta, setFilterMeta] = useState({ states: [], classifications: [] });
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/promotions"); setCampaigns(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadZones = useCallback(async () => {
    try {
      const params = {};
      Object.entries(zoneFilter).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
      const { data } = await api.get("/admin/zones/manage", { params });
      setZones(data.items); setFilterMeta({ states: data.states, classifications: data.classifications });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [zoneFilter]);
  useEffect(() => { if (open) loadZones(); }, [open, loadZones]);

  const toggleAll = (on) => { const m = {}; if (on) zones.forEach((z) => { m[z.zoneId] = true; }); setPicked(m); };
  const pickedIds = Object.keys(picked).filter((k) => picked[k]);

  const launch = async () => {
    if (!form.name.trim()) return toast.error("Give the campaign a name.");
    if (!form.promotional_price) return toast.error("Set a promotional price.");
    if (pickedIds.length === 0) return toast.error("Select at least one zone.");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/promotions", {
        name: form.name, promotion_type: form.promotion_type || form.name,
        promotional_price: Number(form.promotional_price),
        promotion_start_date: form.promotion_start_date || null,
        promotion_end_date: form.promotion_end_date || null,
        zone_ids: pickedIds,
      });
      toast.success(`Campaign launched across ${data.applied} zone(s)`);
      setOpen(false); setForm(EMPTY); setPicked({}); loadCampaigns();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const endCampaign = async (id) => {
    try { await api.post(`/admin/promotions/${id}/end`); toast.success("Campaign ended — promo pricing removed"); loadCampaigns(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="promotions-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold">Promo campaigns</h2>
          <p className="text-sm text-muted-foreground">Launch promotional lease pricing across many zones at once.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-campaign-btn"><Plus className="w-4 h-4 mr-2" />New campaign</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Launch a promo campaign</DialogTitle>
              <DialogDescription>e.g. “First Year $49”, “Georgia Launch”. Applies a promotional price to every selected zone.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Campaign name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Georgia Launch Campaign" data-testid="campaign-name" /></div>
                <div><Label>Promo type (label)</Label><Input value={form.promotion_type} onChange={(e) => setForm({ ...form, promotion_type: e.target.value })} placeholder="Launch" data-testid="campaign-type" /></div>
                <div><Label>Promotional price ($)</Label><Input type="number" value={form.promotional_price} onChange={(e) => setForm({ ...form, promotional_price: e.target.value })} placeholder="49" data-testid="campaign-price" /></div>
                <div />
                <div><Label>Start date</Label><Input type="date" value={form.promotion_start_date} onChange={(e) => setForm({ ...form, promotion_start_date: e.target.value })} data-testid="campaign-start" /></div>
                <div><Label>End date</Label><Input type="date" value={form.promotion_end_date} onChange={(e) => setForm({ ...form, promotion_end_date: e.target.value })} data-testid="campaign-end" /></div>
              </div>

              <div className="rounded-xl border border-border p-3 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-sm">Target zones ({pickedIds.length} selected)</Label>
                  <div className="flex gap-2 items-center">
                    <Select value={zoneFilter.state} onValueChange={(v) => setZoneFilter((p) => ({ ...p, state: v }))}><SelectTrigger className="w-[120px] h-8" data-testid="campaign-filter-state"><SelectValue placeholder="State" /></SelectTrigger>
                      <SelectContent><SelectItem value={ALL}>All states</SelectItem>{filterMeta.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    <Select value={zoneFilter.status} onValueChange={(v) => setZoneFilter((p) => ({ ...p, status: v }))}><SelectTrigger className="w-[130px] h-8" data-testid="campaign-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent><SelectItem value={ALL}>Any status</SelectItem>{["AVAILABLE", "LEASED", "VELOPATHS_MANAGED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleAll(true)} data-testid="campaign-select-all">Select all shown</Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleAll(false)} data-testid="campaign-clear">Clear</Button>
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-border" data-testid="campaign-zone-list">
                  {zones.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No zones match this filter.</p>
                    : zones.map((z) => (
                      <label key={z.zoneId} className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`campaign-zone-${z.zoneId}`}>
                        <Checkbox checked={!!picked[z.zoneId]} onCheckedChange={(v) => setPicked((p) => ({ ...p, [z.zoneId]: !!v }))} />
                        <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{z.zoneName}</div><div className="text-xs text-muted-foreground">{z.state || "—"} · {z.classification} · ${Number(z.listFee).toFixed(0)}/yr · {z.status}</div></div>
                      </label>
                    ))}
                </div>
              </div>
              <Button className="w-full" onClick={launch} disabled={saving} data-testid="campaign-launch">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Launch campaign</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : campaigns.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="promotions-empty"><Megaphone className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><div className="font-heading font-bold">No campaigns yet</div><p className="text-sm text-muted-foreground mt-1">Launch a promo like “First Year $49” across a whole state.</p></div>
        : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="promotions-list">
            {campaigns.map((c) => (
              <div key={c.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`campaign-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-heading font-bold">{c.name}</div><div className="text-xs text-muted-foreground">{c.promotionType} · {c.zoneCount} zone{c.zoneCount === 1 ? "" : "s"}</div></div>
                  <Badge className={c.status === "active" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-muted text-muted-foreground border border-border"}>{c.status}</Badge>
                </div>
                <div className="mt-3 flex items-center gap-2"><Percent className="w-4 h-4 text-rose-400" /><span className="text-xl font-heading font-extrabold text-primary">${Number(c.promotionalPrice).toFixed(0)}</span><span className="text-xs text-muted-foreground">/year promo</span></div>
                <div className="text-xs text-muted-foreground mt-2">{c.startDate || "—"} → {c.endDate || "no end"}</div>
                {c.status === "active" && <Button size="sm" variant="outline" className="mt-3 text-rose-400" onClick={() => endCampaign(c.id)} data-testid={`campaign-end-${c.id}`}>End campaign</Button>}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
